import { Copy, ShieldAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  JEV_ASSESSABLE_LEVEL,
  JEV_ENGLISH_CHIPS,
  JEV_ENGLISH_LABELS,
  JEV_EVIDENCE_LABELS,
  JEV_LEVEL_LABELS,
  JEV_OWNERSHIP_LABELS,
  JEV_TRANSFERABLE_AT,
  hasJevFlags,
  jevEnglishOf,
  jevEvidenceOf,
  jevLevelOf,
  jevPartNameOf,
  jevPartNoteOf,
  type CorrectJevRequest,
  type JevAskPart,
  type JevCorrectionView,
  type JevEvidence,
  type JevEvidenceSummary,
  type JevFlags,
  type JevPartAnswer,
  type JevPersonResult,
  type JevReason,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { Skeleton } from '../../../components/Skeleton';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { useCorrectJev, useJevReason } from '../api/jev-api';
import { useRested } from '../use-rested';
import { CorrectJevMenu, CorrectionLines } from './CorrectJevMenu';
import styles from './jev.module.css';

// ADR 0050, as amended by ADR 0051: what Jev found for one person — in the list (level, number, a grade per part and
// Haiku's one-line reason) and on the candidate sheet ("Why Jev put them Nth": each part's grade, Haiku's point on
// it, up to three lines of the CV, a "watch out", and anything the CV tried). Quotes are lines of their CV that Jev
// pointed at, never words a model wrote. Colour never says it alone: the glyph and the word do.

export const EVIDENCE_GLYPHS: Readonly<Record<JevEvidence, string>> = { strong: '✓✓', shown: '✓', related: '~', named: '?', none: '✗' };
/** The three colours the glyphs already use: shown in green, partly in amber, missing in red. */
export const EVIDENCE_TONES: Readonly<Record<JevEvidence, 'shows' | 'unclear' | 'not_shown'>> = { strong: 'shows', shown: 'shows', related: 'unclear', named: 'unclear', none: 'not_shown' };
/** A part graded below Related is "only named" or "not shown": its quote isn't evidence of real use. */
const RELATED_LEVEL = 1.5;

/** An older stored reason names a line by its joined text ("name — counts: note"); show the short name there too. What is stored stays as it is. */
function plainLinesIn(text: string, parts: readonly JevAskPart[]): string {
  return parts.reduce((out, part) => {
    const name = jevPartNameOf(part.text);
    return name === part.text ? out : out.split(part.text).join(name);
  }, text);
}

export function plainReasonOf(reason: JevReason | null, parts: readonly JevAskPart[]): JevReason | null {
  if (!reason) return null;
  return {
    ...reason,
    summary: plainLinesIn(reason.summary, parts),
    watchOut: reason.watchOut === null ? null : plainLinesIn(reason.watchOut, parts),
    points: reason.points.map((point) => ({ ...point, text: plainLinesIn(point.text, parts), ask: point.ask === null || point.ask === undefined ? point.ask : plainLinesIn(point.ask, parts) })),
  };
}

function evidenceOf(answer: JevPartAnswer | null | undefined): JevEvidence | null {
  return answer ? jevEvidenceOf(answer.level) : null;
}

/** The first quote worth showing: a must-have Jev found real evidence for, then any part. */
function leadQuote(parts: readonly JevAskPart[], answers: readonly (JevPartAnswer | null)[]): string | null {
  const order = parts.map((part, index) => ({ part, index })).sort((a, b) => Number(b.part.isMust) - Number(a.part.isMust));
  for (const { index } of order) {
    const answer = answers[index];
    if (answer && answer.level >= RELATED_LEVEL && answer.quotes[0]) return answer.quotes[0].text;
  }
  return null;
}

/** The flag a row shows: the strongest thing the CV tried. */
function flagLabel(flags: JevFlags): string {
  if (flags.hiddenText !== null) return 'Hidden text removed';
  if (flags.aiLines.length > 0) return 'Spoke to the AI';
  if (flags.copiedLines > 0) return 'Copied the job advert';
  return 'Mostly a keyword list';
}

/** The chip a list row shows for the English in a CV: only the two worst steps, in the words the owner chose (ADR 0058 §D, Q3). */
function englishChip(english: number | null | undefined): string | null {
  return english === null || english === undefined ? null : (JEV_ENGLISH_CHIPS[jevEnglishOf(english)] ?? null);
}

const ENGLISH_NOTE = 'Judged from how the CV is written; it may have been written or polished by someone else — check in conversation.';

const FIT_HINT = 'Fit: how strong the evidence is, where the CV says something. Covers: how much of the ask the CV says anything about (must-haves count double).';

/** ADR 0058 §A: fit and coverage in a few words — "unknown, not a no" when the CV says nothing about the ask. */
function fitLine(evidence: JevEvidenceSummary): string {
  return evidence.fit === null ? 'nothing in the CV on this ask' : `fit ${evidence.fit} · covers ${evidence.coverage}%`;
}

export function notReadLine(result: JevPersonResult | undefined): string {
  if (!result) return 'Arrived after this ask. Run it again to include them.';
  if (result.status === 'unreadable') return 'The CV couldn’t be read. Check it by hand.';
  return 'Jev didn’t answer for this CV. Check it by hand.';
}

function EvidenceGlyph({ evidence }: { readonly evidence: JevEvidence | null }): ReactNode {
  return (
    <span className={styles.glyph} data-verdict={evidence ? EVIDENCE_TONES[evidence] : undefined} aria-label={evidence ? JEV_EVIDENCE_LABELS[evidence] : 'No answer'}>
      {evidence ? EVIDENCE_GLYPHS[evidence] : '—'}
    </span>
  );
}

export function JevCell({ result, parts }: { readonly result: JevPersonResult | undefined; readonly parts: readonly JevAskPart[] }): ReactNode {
  const flag = result?.flags && hasJevFlags(result.flags) ? <Chip tone="danger">{flagLabel(result.flags)}</Chip> : null;
  if (!result || result.status !== 'read' || result.match === null) {
    return (
      <span className={styles.cell}>
        <span className={styles.level}>
          <span className={styles.dot} data-level="none" aria-hidden />
          {result ? 'Not read' : '—'}
          {flag}
        </span>
        <span className={styles.why}>{notReadLine(result)}</span>
      </span>
    );
  }
  const level = jevLevelOf(result.match);
  const quote = leadQuote(parts, result.answers);
  const english = englishChip(result.english);
  return (
    <span className={styles.cell}>
      <span className={styles.level}>
        <span className={styles.dot} data-level={level} aria-hidden />
        {JEV_LEVEL_LABELS[level]}
        <span className={styles.levelNumber}>{result.match}</span>
        {flag}
        {english && <Chip tone="attention">{english}</Chip>}
      </span>
      {result.evidence && (
        <span className={styles.fitLine} title={FIT_HINT}>
          {fitLine(result.evidence)}
        </span>
      )}
      <span className={styles.why}>
        {parts.map((part, index) => {
          const evidence = evidenceOf(result.answers[index]);
          return (
            <span key={part.text} className={styles.verdict} title={`${jevPartNameOf(part.text)}: ${evidence ? JEV_EVIDENCE_LABELS[evidence] : 'No answer'}`}>
              <EvidenceGlyph evidence={evidence} /> {jevPartNameOf(part.text)}
            </span>
          );
        })}
        {result.reason ? <span className={styles.reasonLine}>{result.reason.summary}</span> : quote && <mark className={styles.quote}>“{quote}”</mark>}
      </span>
    </span>
  );
}

const ORDINAL_SUFFIXES: Readonly<Record<number, string>> = { 1: 'st', 2: 'nd', 3: 'rd' };

/** "1st", "2nd", "23rd", "11th". */
function ordinal(place: number): string {
  const teen = place % 100;
  if (teen >= 11 && teen <= 13) return `${place}th`;
  return `${place}${ORDINAL_SUFFIXES[place % 10] ?? 'th'}`;
}

/** How long the sheet stays on a person before Haiku's reason is asked for. */
export const JEV_REASON_REST_MS = 600;

interface JevWhyProps {
  readonly name: string;
  readonly jobId: string;
  readonly askId: string;
  readonly parts: readonly JevAskPart[];
  readonly result: JevPersonResult | undefined;
  /** Their place in Jev's order in this tab, when the list is in Jev's order. */
  readonly place: number | null;
  readonly isPractice: boolean;
  /** ADR 0062: the ask is the ideal candidate's reading, so the match, fit and covers belong to the profile block above, not to this one. */
  readonly isProfile?: boolean;
}

/** RNK-02 (ADR 0051 §6): Haiku's reason, each part's grade with Haiku's point and the CV's lines, and the flags. */
export function JevWhy({ name, jobId, askId, parts, result, place, isPractice, isProfile = false }: JevWhyProps): ReactNode {
  const isRead = result?.status === 'read' && result.match !== null;
  const needsReason = isRead && !result.reason;
  // Someone below the top 50 has no reason yet: Haiku writes it (F23.17) once the sheet rests on them, so stepping
  // through with Previous / Next doesn't pay for a reason per person passed (Redesign Group 3).
  const restedOn = useRested(result?.applicationId, JEV_REASON_REST_MS);
  const asked = useJevReason(jobId, askId, result?.applicationId, needsReason && restedOn === result.applicationId);
  const reason = plainReasonOf(result?.reason ?? asked.data?.reason ?? null, parts);
  const asks = reason?.points.flatMap((point) => (point.ask ? [point.ask] : [])) ?? [];
  const { correct } = useCorrectJev(jobId, askId);
  const applicationId = result?.applicationId;
  const correctFor = (subject: string) => (input: CorrectJevRequest): void => {
    if (applicationId) void correct(applicationId, input, subject);
  };
  return (
    <section className={styles.sheet} aria-label="What Jev found">
      <div className={styles.sheetHead}>
        <span className={styles.tile} data-size="sm" aria-hidden>
          J
        </span>
        <h3 className={styles.sheetTitle}>{place !== null && isRead ? `Why Jev put ${name} ${ordinal(place)}` : 'What Jev found'}</h3>
        {isRead && !isProfile && result.match !== null && (
          <span className={`${styles.level} ${styles.sheetLevel}`}>
            <span className={styles.dot} data-level={jevLevelOf(result.match)} aria-hidden />
            {JEV_LEVEL_LABELS[jevLevelOf(result.match)]}
            <span className={styles.levelNumber}>{result.match}</span>
            <span className={styles.sheetPartKind}>for this ask</span>
          </span>
        )}
      </div>
      {isRead ? (
        <>
          <ReasonSummary reason={reason} isWriting={needsReason && !asked.isError && (restedOn === undefined || asked.fetchStatus === 'fetching')} problem={asked.error ? errorMessage(asked.error) : null} />
          {result.evidence && !isProfile && <FitAndCovers evidence={result.evidence} silentParts={parts.filter((_, index) => (result.answers[index]?.level ?? 0) < JEV_ASSESSABLE_LEVEL)} />}
          {result.english !== null && <EnglishLine english={result.english} />}
          <ol className={styles.sheetParts}>
            {parts.map((part, index) => {
              const point = reason?.points.find((entry) => entry.part === index);
              return (
                <PartFinding
                  key={part.text}
                  part={part}
                  answer={result.answers[index]}
                  point={point?.text ?? null}
                  ask={point?.ask ?? null}
                  corrections={result.corrections.filter((entry) => entry.partIndex === index)}
                  onCorrect={correctFor(part.text)}
                  partIndex={index}
                />
              );
            })}
          </ol>
          {asks.length > 0 && <CopyQuestions asks={asks} />}
          {reason?.watchOut && (
            <p className={styles.watchOut}>
              <span className={styles.watchOutLabel}>Watch out</span> {reason.watchOut}
            </p>
          )}
        </>
      ) : (
        <p className={styles.sheetNote}>{notReadLine(result)}</p>
      )}
      {result?.flags && hasJevFlags(result.flags) && <FlagsBlock flags={result.flags} corrections={result.corrections.filter((entry) => entry.partIndex === null)} onCorrect={correctFor('the flags')} />}
      <p className={styles.sheetNote}>
        {isPractice ? 'Practice mode: a stand-in on this computer matched words — this is not Jev. ' : 'Jev read the whole CV and graded each part; Haiku only put that into words. Jev can be wrong. '}
        Its quotes are marked in the CV.{isProfile ? ' The ranking above is worked out from these findings.' : ' The score from the screening criteria stays the official one.'}
      </p>
    </section>
  );
}

export function ReasonSummary({ reason, isWriting, problem }: { readonly reason: JevReason | null; readonly isWriting: boolean; readonly problem: string | null }): ReactNode {
  if (reason) {
    return (
      <div className={styles.reason}>
        <p className={styles.reasonSummary}>{reason.summary}</p>
        <p className={styles.reasonBy}>{reason.writer === 'practice' ? 'Practice mode: put together from the quotes on this computer — not Haiku.' : 'Written by Haiku from what Jev found.'}</p>
      </div>
    );
  }
  if (problem) return <p className={styles.sheetNote}>{problem}</p>;
  if (!isWriting) return null;
  // The reason has never been on screen: a line shaped like it, while Haiku writes (CLAUDE.md §5a).
  return (
    <div className={styles.reason} aria-busy="true" aria-label="Haiku is writing the reason">
      <Skeleton width="90%" />
      <Skeleton width="60%" />
    </div>
  );
}

/** ADR 0058 §A: what the CV says (fit) and how much of the ask it says anything about. "Unknown, not a no": a silent part is never a failure. */
function FitAndCovers({ evidence, silentParts }: { readonly evidence: JevEvidenceSummary; readonly silentParts: readonly JevAskPart[] }): ReactNode {
  return (
    <ul className={styles.fitRows} aria-label="Fit and what the CV covers">
      <li className={styles.fitRow}>
        <span className={styles.fitHead}>{evidence.fit === null ? 'Fit —' : `Fit ${evidence.fit}`}</span>
        <span className={styles.fitNote}>{evidence.fit === null ? 'The CV says nothing about any part of this ask.' : 'How strong the evidence is, where the CV says something.'}</span>
      </li>
      <li className={styles.fitRow}>
        <span className={styles.fitHead}>Covers {evidence.coverage}% of this ask</span>
        <span className={styles.fitNote}>
          {silentParts.length === 0 ? 'Says something about every part.' : `Says nothing about: ${silentParts.map((part) => jevPartNameOf(part.text)).join(', ')}. Unknown, not a no — ask them.`}
        </span>
      </li>
    </ul>
  );
}

/** ADR 0058 §D: the English the CV is written in — a plain line, shown only; it never moves anyone. */
function EnglishLine({ english }: { readonly english: number }): ReactNode {
  return (
    <div className={styles.fitRow}>
      <span className={styles.fitHead}>English in the CV: {JEV_ENGLISH_LABELS[jevEnglishOf(english)]}</span>
      <span className={styles.fitNote}>{ENGLISH_NOTE}</span>
    </div>
  );
}

/** ADR 0058 §C: every “Ask them” question of this sheet, numbered, on the clipboard in one click. */
function CopyQuestions({ asks }: { readonly asks: readonly string[] }): ReactNode {
  const showToast = useToasts((state) => state.show);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(asks.map((ask, index) => `${index + 1}. ${ask}`).join('\n'));
      showToast({ tone: 'success', message: 'Questions copied' });
    } catch {
      showToast({ tone: 'danger', message: "The questions weren't copied", detail: 'This browser blocked the clipboard. Try again from the page itself.' });
    }
  };
  return (
    <div className={styles.copyRow}>
      <Button variant="quiet" size="sm" icon={Copy} onClick={() => void copy()}>
        Copy questions
      </Button>
    </div>
  );
}

interface PartFindingProps {
  readonly part: JevAskPart;
  readonly answer: JevPartAnswer | null | undefined;
  readonly point: string | null;
  readonly ask: string | null;
  readonly partIndex: number;
  readonly corrections: readonly JevCorrectionView[];
  readonly onCorrect: (input: CorrectJevRequest) => void;
}

function PartFinding({ part, partIndex, answer, point, ask, corrections, onCorrect }: PartFindingProps): ReactNode {
  const evidence = evidenceOf(answer);
  const quotes = evidence && evidence !== 'none' ? (answer?.quotes ?? []) : [];
  // ADR 0058 §B–§C: only where the CV shows something (Related or better) is there an own part or an equivalent to speak of.
  const isShown = !!answer && answer.level >= RELATED_LEVEL;
  const name = jevPartNameOf(part.text);
  const note = jevPartNoteOf(part.text);
  const ownership = isShown && answer.ownership ? JEV_OWNERSHIP_LABELS[answer.ownership] : null;
  const isEquivalent = isShown && (answer.transferable ?? 0) >= JEV_TRANSFERABLE_AT;
  return (
    <li className={styles.sheetPart}>
      <EvidenceGlyph evidence={evidence} />
      <span>
        <span className={styles.sheetPartLabel}>{name}</span>
        <span className={styles.sheetPartKind}>{part.isMust ? 'Must show' : 'Better if'}</span>
        <span className={styles.evidence} data-verdict={evidence ? EVIDENCE_TONES[evidence] : undefined}>
          {evidence ? JEV_EVIDENCE_LABELS[evidence] : 'No answer'}
        </span>
        {note && <span className={styles.sheetPartNote}>{note}</span>}
      </span>
      {(ownership || isEquivalent) && (
        <span className={styles.sheetChips}>
          {ownership && (
            <Chip plain tone="neutral">
              {ownership}
            </Chip>
          )}
          {isEquivalent && (
            <Chip plain tone="progress">
              Through an equivalent
            </Chip>
          )}
        </span>
      )}
      {point && <p className={styles.sheetPoint}>{point}</p>}
      {quotes.length > 0 ? (
        <ul className={styles.sheetQuotes} aria-label={`Lines from the CV for ${name}`}>
          {quotes.map((quote) => (
            <li key={`${quote.line}:${quote.text}`}>
              <mark className={styles.quote}>“{quote.text}”</mark>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.sheetQuote}>{evidence === 'none' ? 'The CV doesn’t mention it — unknown, not a no.' : 'No line in the CV shows it.'}</p>
      )}
      {ask && <p className={styles.sheetAsk}>Ask them: {ask}</p>}
      <div className={styles.sheetCorrect}>
        <CorrectionLines corrections={corrections} />
        <CorrectJevMenu subject={part.text} partIndex={partIndex} currentGrade={evidence} onCorrect={onCorrect} />
      </div>
    </li>
  );
}

/** ADR 0051 §1–§2: what the CV tried. The person keeps their place — what they tried was removed before Jev read. */
function FlagsBlock({ flags, corrections, onCorrect }: { readonly flags: JevFlags; readonly corrections: readonly JevCorrectionView[]; readonly onCorrect: (input: CorrectJevRequest) => void }): ReactNode {
  const [isHiddenShown, setHiddenShown] = useState(false);
  const isSteering = flags.hiddenText !== null || flags.aiLines.length > 0 || flags.copiedLines > 0;
  return (
    <div className={styles.flags} role="note">
      <p className={styles.flagsTitle}>
        <ShieldAlert size={16} aria-hidden />
        {isSteering ? 'This CV tried to steer the ranking' : 'Worth a closer look'}
      </p>
      <ul className={styles.flagsList}>
        {flags.hiddenText !== null && (
          <li>
            Text hidden in the file (white on white, or too small to read) was taken out before anything read it.{' '}
            <button type="button" className={styles.flagsToggle} aria-expanded={isHiddenShown} onClick={() => setHiddenShown((shown) => !shown)}>
              {isHiddenShown ? 'Hide it' : 'Show what was hidden'}
            </button>
            {isHiddenShown && <blockquote className={styles.hiddenText}>{flags.hiddenText}</blockquote>}
          </li>
        )}
        {flags.aiLines.length > 0 && (
          <li>
            {flags.aiLines.length === 1 ? 'A line spoke to an AI and was taken out:' : `${flags.aiLines.length} lines spoke to an AI and were taken out:`}
            {flags.aiLines.map((line) => (
              <q key={line} className={styles.flagLine}>
                {line}
              </q>
            ))}
          </li>
        )}
        {flags.copiedLines > 0 && <li>{flags.copiedLines === 1 ? 'A line copied from the job advert was set aside.' : `${flags.copiedLines} lines copied from the job advert were set aside.`}</li>}
        {flags.isKeywordList && <li>Jev found the CV is mostly a list of keywords, with little about what they actually did.</li>}
      </ul>
      {isSteering && <p className={styles.sheetNote}>They keep their place: what they tried was removed first, so it gained them nothing.</p>}
      <div className={styles.sheetCorrect}>
        <CorrectionLines corrections={corrections} />
        <CorrectJevMenu subject="the flags" partIndex={null} currentGrade={null} onCorrect={onCorrect} />
      </div>
    </div>
  );
}

/** Under the tabs while an ask runs (mockup "Jev is reading 1,000 CVs for this ask"). */
export function JevReadingLine({ done, total }: { readonly done: number; readonly total: number }): ReactNode {
  const share = total === 0 ? 0 : Math.min(1, done / total);
  return (
    <div className={styles.reading} role="status">
      <span className={styles.readingLead}>
        Jev is reading {total.toLocaleString('en-US')} CVs for this ask — {done.toLocaleString('en-US')} done
      </span>
      <span className={styles.readingNote}>The list stays as it is until the new order is ready.</span>
      <span className={styles.readingTrack} aria-hidden>
        <span className={styles.readingFill} style={{ inlineSize: `${Math.round(share * 100)}%` }} />
      </span>
    </div>
  );
}
